// Custom Document wizard state. Two steps: input → edit/export.

export interface CustomState {
  step: 1 | 2;
  maxReachedStep: 1 | 2;
  docName: string;
  initialContent: string;
  currentSlug: string | null;
  /** ID of the export template selected on Step 2. */
  exportTemplate: string;
}

export const INITIAL_CUSTOM_STATE: CustomState = {
  step: 1,
  maxReachedStep: 1,
  docName: "",
  initialContent: "",
  currentSlug: null,
  exportTemplate: "plain",
};

const STORAGE_KEY = "zww-agent.custom-wizard-state";

export function loadPersistedCustomState(): CustomState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_CUSTOM_STATE;
    const parsed = JSON.parse(raw) as Partial<CustomState>;
    return { ...INITIAL_CUSTOM_STATE, ...parsed };
  } catch {
    return INITIAL_CUSTOM_STATE;
  }
}

export function persistCustomState(state: CustomState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function clearPersistedCustomState(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Match the legacy `buildCustomSlug` exactly. */
export function buildCustomSlug(name: string): string {
  const ts = new Date().toISOString().replace(/[^0-9]/g, "").slice(0, 14);
  const slugPart = ((name || "dokument")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40)) || "dokument";
  return `${slugPart}_${ts}`;
}

/**
 * Convert an uploaded filename into a default document name (no extension,
 * symbols replaced with underscores, trimmed).
 */
export function fileNameToDocName(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, "");
  const cleaned = base
    .replace(/[^a-zA-Z0-9_äöüÄÖÜß\- ]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .trim();
  return cleaned || "";
}

const VALID_NAME_RE = /^[a-z0-9_äöüÄÖÜß\- ]+$/i;
export function isValidDocName(name: string): boolean {
  return VALID_NAME_RE.test(name.trim());
}
